import * as React from 'react'
import { Formik, FieldProps, Field } from 'formik'
import isEmail from 'validator/lib/isEmail'
import * as Yup from 'yup'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/next'
import * as env from '@owl-nest/config'
import * as service from '@owl-nest/services'
import { t } from '@owl-nest/localize'
import { utils as authUtils } from '@owl-nest/auth'

import * as S from './styles'
import { useNewPasswordValidator } from './useNewPasswordValidator'
import { useUserWithToken } from './hooks/useUserWithToken'

type RequestResetPasswordForm = { identifier: string }
type ResetPasswordConfirmForm = { password: string; password_confirmation: string }

export function ResetPassword(): React.ReactElement<any> {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('token') ?? undefined

  if (token) {
    const user = useUserWithToken(token)

    if (user !== undefined) {
      return <ResetPasswordConfirmationForm user={user} token={token} />
    }
  }

  return <ResetPasswordForm />
}

function ResetPasswordForm(): React.ReactElement<any> {
  const urlParams = new URLSearchParams(window.location.search)
  const originUrl = urlParams.get('origin_url') ?? env.SIGNUP_URL

  const [loading, setLoading] = React.useState(false)

  const resetPasswordSchema = Yup.object().shape({
    identifier: Yup.string()
      .required(t('This field is required'))
      .test('is-valid-email', t('Enter a valid email address'), (value) => {
        if (!value) return false
        return isEmail(authUtils.getImpostorFreeEmail(value).email)
      }),
  })

  return (
    <Formik<RequestResetPasswordForm>
      initialValues={{ identifier: '' }}
      validationSchema={resetPasswordSchema}
      onSubmit={(values) => {
        setLoading(true)
        service.resetPassword.sendResetPasswordRequest(values.identifier).then(() => {
          document.location.href = `${env.SIGNIN_URL}?resetRequested=true`
        })
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit }) => {
        return (
          <S.ResetPasswordForm noValidate onSubmit={handleSubmit}>
            <Field name="identifier">
              {({ field, meta }: FieldProps<string>) => {
                return (
                  <plume.TextField
                    label={t('E-mail address')}
                    placeholder={t('E-mail address')}
                    type="email"
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={meta.touched ? meta.error : undefined}
                  />
                )
              }}
            </Field>

            <plume.RoundButton loading={loading} fitToParent type="submit">
              {t('Reset my password')}
            </plume.RoundButton>

            <plume.Link href={originUrl} kind="secondary">
              {t('Back')}
            </plume.Link>
          </S.ResetPasswordForm>
        )
      }}
    </Formik>
  )
}

function ResetPasswordConfirmationForm({
  user,
  token,
}: {
  user: api.PublicUser
  token: string
}): React.ReactElement<any> {
  const [loading, setLoading] = React.useState(false)
  const [feedback, validateNewPassword] = useNewPasswordValidator()

  const resetPasswordConfirmationSchema = Yup.object().shape({
    password: Yup.string().required(t('This field is required')),
    password_confirmation: Yup.string()
      .required(t('This field is required'))
      .oneOf([Yup.ref('password')], t('The passwords do not match. Please check and try again.')),
  })

  return (
    <Formik<ResetPasswordConfirmForm>
      initialValues={{ password: '', password_confirmation: '' }}
      validationSchema={resetPasswordConfirmationSchema}
      onSubmit={(values) => {
        setLoading(true)
        service.resetPassword.updatePassword(user.id, values.password, values.password_confirmation, token).then(() => {
          document.location.href = `${env.SIGNIN_URL}?resetSuccess=true`
        })
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit, validateField }) => {
        return (
          <S.ResetPasswordForm noValidate onSubmit={handleSubmit}>
            <Field
              name="password"
              validate={(value: string) => {
                if (!value) {
                  return t('This field is required')
                } else {
                  return validateNewPassword(value)
                }
              }}
            >
              {({ field, meta }: FieldProps<string>) => {
                React.useEffect(() => {
                  if (field.value !== '') {
                    validateField(field.name)
                  }
                }, [field.value])

                return (
                  <plume.PasswordField
                    autoFocus
                    label={t('New password')}
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    feedback={field.value ? feedback : undefined}
                    error={meta.touched && (meta.error === 'error' ? true : meta.error)}
                  />
                )
              }}
            </Field>

            <Field name="password_confirmation">
              {({ field, meta }: FieldProps<string>) => {
                return (
                  <plume.PasswordField
                    label={t('New password confirmation')}
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    feedback={undefined}
                    error={meta.touched ? meta.error : undefined}
                  />
                )
              }}
            </Field>

            <plume.RoundButton loading={loading} fitToParent type="submit">
              {t('Reset my password')}
            </plume.RoundButton>
          </S.ResetPasswordForm>
        )
      }}
    </Formik>
  )
}
